import FARM_TYPE from 'Const/farm/type.json';
import STATUS from 'Const/farm/status.json';
import { BaseReactiveClass } from './BaseReactiveClass';

export class FarmClass extends BaseReactiveClass {
  static TEMPLATE_SPONSOR = 'sponsor';

  static DOCUMENT_SUBSCRIPTION_AGREEMENT = 'subscription_agreement';
  static DOCUMENT_OPERATING_AGREEMENT = 'operating_agreement';

  get route() {
    return { name: 'farm', params: { slug: this.slug } };
  }

  get url() {
    return `/${this.slug}`;
  }

  get isShareType() {
    return [FARM_TYPE.INVEST, FARM_TYPE.PLEDGE].includes(this.type);
  }

  get isUnitType() {
    return [FARM_TYPE.INVEST_UNIT, FARM_TYPE.PLEDGE_UNIT].includes(this.type);
  }

  get isPledgeType() {
    return [FARM_TYPE.PLEDGE, FARM_TYPE.PLEDGE_UNIT].includes(this.type);
  }

  get isInvestType() {
    return [FARM_TYPE.INVEST, FARM_TYPE.INVEST_UNIT].includes(this.type);
  }

  get isInvestUnitType() {
    return [FARM_TYPE.INVEST_UNIT].includes(this.type);
  }

  get isDummyType() {
    return [FARM_TYPE.DUMMY].includes(this.type);
  }

  get isTemplateSponsor() {
    return this.template === FarmClass.TEMPLATE_SPONSOR;
  }

  get assetTypeTitle() {
    return this.isUnitType ? 'Unit' : 'Share';
  }

  getAssetTypeTitlePlural(investment = 0) {
    return this.assetTypeTitle + (investment > 1 ? 's' : '');
  }

  get numberOfSharesTitle() {
    return `Number of ${this.assetTypeTitle}s`;
  }

  get pricePerShareTitle() {
    return `Price per ${this.assetTypeTitle}`;
  }

  get pricePerAcreTitle() {
    return `Price per ${this.isUnitType ? 'Unit' : 'Acre'}`;
  }

  get netAnnualReturn() {
    if (this.net_irr_max > 0) return `${this.net_irr} - ${this.net_irr_max}%`;
    return `${this.net_irr}%`;
  }

  get isTimber() {
    return this.farm_crop_type === 'timber';
  }

  get thumbnailUrl() {
    return this.thumbnail?.thumbnail?.url
      || '';
  }

  get thumbnailFullUrl() {
    return this.thumbnail?.full?.url
      || '';
  }

  get thumbnailLargeUrl() {
    return this.thumbnail?.large?.url
      || '';
  }

  get thumbnailSmallUrl() {
    return this.thumbnail?.small?.url
      || '';
  }

  get thumbnailSliderUrl() {
    return this.thumbnail?.slider?.url
      || '';
  }

  get thumbnailAlt() {
    return `${this.name} thumbnail`;
  }

  get thumbnailTitle() {
    return this.thumbnail?.original?.meta_data?.name
      || this.thumbnail?.original?.name || '';
  }

  get thumbnailPlaceholderUrl() {
    return this.thumbnail?.placeholder?.url;
  }

  get location() {
    return `${ this.county }, ${ this.state }`;
  }

  get isNewDocument() {
    return this.updates_document_folder?.some(doc => !(doc?.meta_data?.read));
  }

  get isNewFinancialStatementDocument() {
    return this.financial_statement_document_folder?.some(doc => !(doc?.meta_data?.read));
  }

  get financialStatementDocumentLast() {
    return this.financial_statement_document_folder?.reduce((lastDoc, currentDoc) => {
      if (new Date(currentDoc?.meta_data?.prepared) > new Date(lastDoc?.meta_data?.prepared || 0)) return currentDoc;
      return lastDoc;
    }, null);
  }

  get documentsToSignTypes() {
    const list = [FarmClass.DOCUMENT_SUBSCRIPTION_AGREEMENT];
    // if farm template is sponsor, it's required operation agreement
    if (this.isTemplateSponsor && this.isInvestUnitType) list.push(FarmClass.DOCUMENT_OPERATING_AGREEMENT);
    return list;
  }

  get documentFolder() {
    return this.document_folder || [];
  }

  get operatingAgreementDocument() {
    return this.documentFolder?.[FarmClass.DOCUMENT_OPERATING_AGREEMENT] || {};
  }

  get subscriptionAgreementDocument() {
    return this.documentFolder?.[FarmClass.DOCUMENT_SUBSCRIPTION_AGREEMENT] || {};
  }

  get isOperatingAgreementDocumentPublic() {
    return this.operatingAgreementDocument?.meta_data?.public || false;
  }

  get isSubscriptionAgreementDocumentPublic() {
    return this.subscriptionAgreementDocument?.meta_data?.public || false;
  }

  get unitAmount() {
    return this.raised / this.units;
  }

  get minAmount() {
    if (this.isUnitType) return this.unitAmount * this.min_investment;
    return this.min_shares_amount;
  }

  get isStatusNew() {
    return this.status === STATUS.NEW;
  }

  get isStatusPending() {
    return this.status === STATUS.PENDING;
  }

  get isStatusApproves() {
    return this.status === STATUS.APPROVED;
  }

  get isStatusDeclined() {
    return this.status === STATUS.DECLINED;
  }

  get isStatusApprovedNCP() {
    return this.status === STATUS.APPROVED_NCP;
  }

  get isStatusApprovedNCD() {
    return this.status === STATUS.DECLINED_NCD;
  }

  get isStatusVerified() {
    return this.status === STATUS.VERIFIED;
  }

  get isStatusPreview() {
    return this.status === STATUS.PREVIEW;
  }

  get isStatusVerifiedHold() {
    return this.status === STATUS.VERIFIED_HOLD;
  }

  get isStatusSuccessfulyClosed() {
    return this.status === STATUS.SUCCESSFULY_CLOSED;
  }

  get isStatusUnsuccessfulyClosed() {
    return this.status === STATUS.UNSUCESSFULY_CLOSED;
  }

  get isStatusSold() {
    return this.status === STATUS.SOLD;
  }

  get isStatusExited() {
    return this.status === STATUS.EXITED;
  }

  get farmSubscribedPercent() {
    // if status verified hold, means farm can have free shares, but can be reached only by special link
    if (this.isStatusVerifiedHold) return 100;
    return Number(this.percent_subscribed) || 0;
  }

  get cropTypeTitle() {
    if (this.farm_crop_type === 'row') return 'Row crop';
    if (this.isTimber) return 'Timber';
    return 'PERMANENT crop';
  }

  get farmPrice() {
    if (this.isUnitType) return this.raised;
    return this.price;
  }

  get restrictEntities() {
    return Array.isArray(this.restrict_entities) ? this.restrict_entities : [];
  }

  get subscribed() {
    return Math.ceil((this.subscribed_shares * 100) / (this.area_size * 10));
  }

  get isUserInvested() {
    return Boolean(this.invest);
  }

  get investmentType() {
    if (this.return_profile === 'core_plus') return 'core plus';
    return this.return_profile || '';
  }

  get dealManagement() {
    if (this.deal_management === 'acretrader_management') return 'AcreTrader Management';
    if (this.isStatusExited || this.isStatusSold || this.isStatusSuccessfulyClosed) return this.sponsor_name;
    return 'Sponsor Management';
  }
}
